<template>
  <div class="q-form">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">{{ isEdit ? `Edit question: ${form.id}` : "Create question" }}</h4>
      <router-link :to="{ name: 'offer-questions-list' }" class="btn btn-default btn-sm">Back to list</router-link>
    </div>

    <div class="q-form__layout">
      <!-- Main -->
      <div class="q-form__main">
        <card class="q-form__card">
          <el-tabs v-model="activeLang" type="card">
            <el-tab-pane v-for="l in langs" :key="l" :name="l">
              <template slot="label">
                <span>
                  {{ l.toUpperCase() }}
                  <span v-if="form.title[l]" class="dot-filled" />
                </span>
              </template>
            </el-tab-pane>
          </el-tabs>

          <div class="q-form__editor-wrap" :dir="dir">
            <label>Title ({{ activeLang.toUpperCase() }}) *</label>
            <el-input v-model="form.title[activeLang]" placeholder="Question title" />

            <label class="mt-3">Label ({{ activeLang.toUpperCase() }})</label>
            <el-input v-model="form.label[activeLang]" placeholder="Short label" />

            <label class="mt-3">Description ({{ activeLang.toUpperCase() }})</label>
            <el-input
              v-model="form.description[activeLang]"
              type="textarea"
              :autosize="{ minRows: 2, maxRows: 6 }"
              placeholder="Description"
            />
          </div>
        </card>

        <!-- Answers -->
        <card title="Answers" class="q-form__card mt-3">
          <div v-for="(a, i) in form.answers" :key="a.id || a._key" class="q-form__answer">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong>Answer #{{ i + 1 }}</strong>
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeAnswer(i)">Remove</el-button>
            </div>

            <div :dir="dir">
              <label>Title ({{ activeLang.toUpperCase() }})</label>
              <el-input v-model="a.title[activeLang]" size="small" placeholder="Answer title" />

              <label class="mt-2">Short description ({{ activeLang.toUpperCase() }})</label>
              <el-input v-model="a.shortDescription[activeLang]" size="small" placeholder="Short description" />

              <label class="mt-2">Description ({{ activeLang.toUpperCase() }})</label>
              <el-input
                v-model="a.description[activeLang]"
                type="textarea"
                size="small"
                :autosize="{ minRows: 1, maxRows: 4 }"
                placeholder="Description"
              />

              <label class="mt-2">Clinics match text ({{ activeLang.toUpperCase() }})</label>
              <el-input v-model="a.clinicsMatchText[activeLang]" size="small" placeholder="Clinics match text" />
            </div>

            <div class="d-flex align-items-center mt-2" style="gap: 12px;">
              <div>
                <label>Tag</label>
                <el-select
                  v-model="a.tag"
                  size="small"
                  placeholder="tag"
                  clearable
                  allow-create
                  filterable
                  style="width: 200px"
                >
                  <el-option v-for="t in tagValues" :key="t" :label="t" :value="t" />
                </el-select>
              </div>
              <div style="flex: 1;">
                <label>Image</label>
                <question-image-upload v-model="a.image" target="answer" />
              </div>
            </div>

            <label class="mt-2">Answer filters</label>
            <question-filters-editor v-model="a.filters" />
          </div>

          <el-button plain icon="el-icon-plus" @click="addAnswer">Add answer</el-button>
        </card>
      </div>

      <!-- Sidebar -->
      <div class="q-form__sidebar">
        <card title="Settings" class="q-form__card">
          <label>Status</label>
          <el-select
            v-model="form.status"
            placeholder="Status"
            style="width: 100%"
            class="mb-3"
            filterable
            allow-create
          >
            <el-option v-for="s in statuses" :key="s.value" :label="s.label" :value="s.value" />
          </el-select>

          <label>Weight</label>
          <el-input v-model="form.weight" placeholder="e.g. 10" class="mb-3" />

          <label>Display type</label>
          <el-input v-model="form.displayType" placeholder="display_type" class="mb-3" />

          <label>Image</label>
          <div class="mb-3">
            <question-image-upload v-model="form.image" target="question" />
          </div>

          <label>ID</label>
          <el-input v-model="form.id" :disabled="isEdit" :placeholder="isEdit ? '' : 'Auto-generated by server'" />
        </card>

        <card title="Question filters" class="q-form__card mt-3">
          <question-filters-editor v-model="form.filters" />
        </card>

        <div class="mt-3">
          <el-button type="primary" :loading="saving" @click="save" style="width: 100%">
            {{ isEdit ? "Save Changes" : "Create" }}
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import APIOfferQuestions from "@/api/modules/offer-questions";
import QuestionFiltersEditor from "./QuestionFiltersEditor.vue";
import QuestionImageUpload from "./QuestionImageUpload.vue";

const TAG_VALUES = ["general", "general_illness", "general_procedure"];
const STATUSES = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Pending", value: "pending" },
];

const emptyTr = (langs = []) => langs.reduce((acc, l) => ({ ...acc, [l]: "" }), {});

export default {
  components: { QuestionFiltersEditor, QuestionImageUpload },
  props: {
    id: { type: String, default: "" },
  },
  data() {
    return {
      tagValues: TAG_VALUES,
      statuses: STATUSES,
      activeLang: "en",
      saving: false,
      form: {
        id: "",
        revision: 0,
        title: {},
        label: {},
        description: {},
        status: "active",
        weight: "",
        displayType: "",
        image: "",
        filters: [],
        answers: [],
      },
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.id);
    },
    langs() {
      return (this.$store.state.cmsApiCatalogs.domains || [])
        .map(({ language }) => language)
        .filter(Boolean);
    },
    dir() {
      return this.activeLang === "ar" ? "rtl" : "ltr";
    },
  },
  methods: {
    ...mapActions("offerQuestions", ["createQuestion", "updateQuestion"]),
    ...mapActions("cmsApiCatalogs", ["getDomains"]),
    initLangFields() {
      this.form.title = { ...emptyTr(this.langs), ...(this.form.title || {}) };
      this.form.label = { ...emptyTr(this.langs), ...(this.form.label || {}) };
      this.form.description = { ...emptyTr(this.langs), ...(this.form.description || {}) };
      this.form.answers.forEach((a) => this.initAnswerLangs(a));
      if (this.langs.length && !this.langs.includes(this.activeLang)) {
        this.activeLang = this.langs[0];
      }
    },
    initAnswerLangs(a) {
      a.title = { ...emptyTr(this.langs), ...(a.title || {}) };
      a.shortDescription = { ...emptyTr(this.langs), ...(a.shortDescription || {}) };
      a.description = { ...emptyTr(this.langs), ...(a.description || {}) };
      a.clinicsMatchText = { ...emptyTr(this.langs), ...(a.clinicsMatchText || {}) };
    },
    addAnswer() {
      const a = {
        id: "",
        _key: Math.random().toString(36),
        title: {},
        shortDescription: {},
        description: {},
        clinicsMatchText: {},
        tag: "",
        image: "",
        filters: [],
      };
      this.initAnswerLangs(a);
      this.form.answers.push(a);
    },
    removeAnswer(i) {
      this.form.answers.splice(i, 1);
    },
    async load() {
      if (!this.isEdit) return;
      this.$store.dispatch("setLoader", true);
      try {
        // Prefer the row stashed by the list when clicking Edit — no network
        // round-trip and no dependency on a by-id fetch the offers service may
        // not support. Fall back to fetching only on a direct URL hit / refresh.
        let q = null;
        const cached = this.$store.state.offerQuestions.question;
        if (cached && cached.id === this.id) {
          q = cached;
        } else {
          // Direct URL / refresh — GET /admin/questions/{id} works directly.
          const response = await APIOfferQuestions.getQuestion(this.id);
          q = (response && response.data && (response.data.question || response.data)) || null;
        }
        if (!q || !q.id) {
          this.$notify({ type: "warning", title: "Question not found" });
          this.$router.push({ name: "offer-questions-list" });
          return;
        }
        this.form.id = q.id;
        this.form.revision = q.revision || 0;
        this.form.title = q.title || {};
        this.form.label = q.label || {};
        this.form.description = q.description || {};
        this.form.status = q.status || "active";
        this.form.weight = q.weight != null ? String(q.weight) : "";
        this.form.displayType = q.display_type || q.displayType || "";
        this.form.image = q.image || "";
        this.form.filters = (q.filters || []).map((f) => ({ ...f }));
        this.form.answers = (q.answers || []).map((a) => ({
          id: a.id || "",
          _key: Math.random().toString(36),
          title: a.title || {},
          shortDescription: a.shortDescription || {},
          description: a.description || {},
          clinicsMatchText: a.clinicsMatchText || {},
          tag: a.tag || "",
          image: a.image || "",
          filters: (a.filters || []).map((f) => ({ ...f })),
        }));
        this.initLangFields();
      } finally {
        this.$store.dispatch("setLoader", false);
      }
    },
    cleanTr(obj) {
      const out = {};
      Object.keys(obj || {}).forEach((l) => {
        const v = (obj[l] || "").trim();
        if (v) out[l] = v;
      });
      return out;
    },
    cleanFilters(filters) {
      return (filters || [])
        .filter((f) => f.key && f.value)
        .map((f) => ({ key: f.key, value: f.value, action: f.action || "", weight: Number(f.weight) || 0 }));
    },
    buildPayload() {
      return {
        revision: this.form.revision,
        title: this.cleanTr(this.form.title),
        label: this.cleanTr(this.form.label),
        description: this.cleanTr(this.form.description),
        status: this.form.status,
        weight: this.form.weight,
        display_type: this.form.displayType,
        image: this.form.image,
        filters: this.cleanFilters(this.form.filters),
        answers: this.form.answers.map((a) => ({
          id: a.id || "",
          title: this.cleanTr(a.title),
          shortDescription: this.cleanTr(a.shortDescription),
          description: this.cleanTr(a.description),
          clinicsMatchText: this.cleanTr(a.clinicsMatchText),
          tag: a.tag || null,
          image: a.image || "",
          filters: this.cleanFilters(a.filters),
        })),
      };
    },
    async save() {
      if (!this.form.title.en || !this.form.title.en.trim()) {
        this.activeLang = "en";
        return this.$notify({ type: "warning", title: "English title is required" });
      }
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.isEdit) {
          await this.updateQuestion({ id: this.form.id, data: payload });
        } else {
          await this.createQuestion(payload);
        }
        this.$notify({ type: "success", title: this.isEdit ? "Question updated" : "Question created" });
        this.$router.push({ name: "offer-questions-list" });
      } catch (e) {
        this.$notify({ type: "warning", title: e.response && e.response.status === 409 ? "Question changed. Reload before saving." : "Error saving question" });
      } finally {
        this.saving = false;
      }
    },
  },
  async created() {
    await this.getDomains();
    this.initLangFields();
    this.load();
  },
};
</script>

<style scoped>
.q-form__layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}
.q-form__main {
  flex: 1;
  min-width: 0;
}
.q-form__sidebar {
  width: 340px;
  flex-shrink: 0;
}
.q-form__card {
  border-radius: 12px;
}
.q-form__editor-wrap {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  padding: 12px;
  background: #fcfdff;
}
.q-form__editor-wrap label,
.q-form__answer label {
  display: inline-block;
  margin-bottom: 6px;
  font-weight: 600;
}
.q-form__answer {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
  background: #fcfdff;
}
.dot-filled {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #67c23a;
  margin-left: 4px;
  vertical-align: middle;
}
</style>
